/**
 * Route guards. Both are courtesy in the browser and enforced again by the
 * API: `RequireAuth` keeps a signed-out person on the sign-in page, and
 * `RequireRole` keeps a signed-in person off a page the API would 403 anyway
 * — with a clear message instead of a broken screen.
 */

import type { Role } from '@unitedos/shared';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { addressOf } from '../lib/returnTo.js';
import { useSessionContext } from '../lib/session.js';

export function RequireAuth({ children }: { children: ReactNode }) {
  const { session } = useSessionContext();
  const location = useLocation();

  if (session === undefined) return <p className="text-muted page-pad">Checking your session…</p>;
  // The whole address, query string included: a deep link's parameters are the point of it (design 11, B9a).
  if (session === null) return <Navigate to="/signin" replace state={{ from: addressOf(location) }} />;
  return <>{children}</>;
}

export function RequireRole({ role, children }: { role: Role; children: ReactNode }) {
  const { me } = useSessionContext();

  if (me.state === 'idle' || me.state === 'loading') return <p className="text-muted">Loading…</p>;
  if (me.state === 'error') {
    return (
      <div className="callout callout--bad">
        <strong>Could not load your account.</strong>
        <p className="text-muted">{me.message}</p>
      </div>
    );
  }
  if (!me.me.roles.includes(role)) return <NoAccess role={role} />;
  return <>{children}</>;
}

export function NoAccess({ role }: { role: Role }) {
  return (
    <section>
      <h1>No access</h1>
      <p>
        This page needs the <strong>{role}</strong> role, and your account does not have it.
      </p>
      <p className="text-muted">If you think it should, ask an admin to add it for you in People &amp; roles.</p>
    </section>
  );
}
